import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Grid,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  TextField,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import SendIcon from "@material-ui/icons/Send";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import RefreshIcon from "@material-ui/icons/Refresh";
import { Alert } from "@material-ui/lab";
import { LoadingSpinner } from "../../../utils/LoadingSpinner";
import { ValidationUtils } from "../utils/ValidationUtils";
import { parseMailingAddress } from "../utils/AddressParser";
import { getCurrentDateString, generateMessageId, convertToPST } from "../utils/PrescriptionHelpers";
import PatientDetailsForm from "../components/PatientDetailsForm";
import PlaceOfServiceSelector from "../components/PlaceOfServiceSelector";
import PrescriberSelector from "../components/PrescriberSelector";
import MedicationSelector from "../components/MedicationSelector";
import PharmacyDetails from "../components/PharmacyDetails";
import { buildNewRxPayload, buildRenewalRxPayload, buildRenewalRxFileForS3 } from "../utils/PayloadBuilders";
import {
  useS3Operations,
  useUPMSIngestion,
  useCustomerCreation,
  useCustomerOptions,
  usePrescriptionForm,
} from "../hooks";
import { COLORS } from "../styles/theme";
import {
  CcsReviewPanel,
  CreateOrderPanel,
  DataAccordion,
  StyledActionButton,
  HealthProfileSection,
} from "../components";
import { IFollowUpIssueInput } from "../../prescription_lifecycle/components/prescription/followUp";
import { PREDEFINED_ADDRESSES } from "../config/addresses";

interface IReviewedPrescription {
  prescriptionId: string;
  response: any;
}

interface IRoutedFollowUp {
  prescriptionId: string;
  issues: IFollowUpIssueInput[];
}

interface IProps {
  data?: any;
}

const STEP = {
  UPLOAD_NEWRX: 0,
  INGEST_NEWRX: 1,
  APPROVE_NEWRX: 2,
  ORDER_NEWRX: 3,
  INGEST_RENEWAL: 4,
  REVIEW_RENEWAL: 5,
  ORDER_RENEWAL: 6,
  COMPLETE: 7,
};

const RPH_CHECK_HINT =
  "Allow ~2 mins after ingestion for the prescription to reach RphCheck in CCS. Click Refresh if it is not found yet.";

/**
 * Renewal Workflow. A renewal answers a prior NewRx, so the NewRx is approved and ordered
 * (its single fill is used up) before the RxRenewalResponse is ingested. The renewal is then
 * approved and ordered, or routed to follow-up.
 */
const UnifiedRenewalWorkflowSection: React.FC<IProps> = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [emailPrefix, setEmailPrefix] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [s3FileName, setS3FileName] = useState("");
  const [isUpdatingFields, setIsUpdatingFields] = useState(false);
  const [selectedAddressIndex, setSelectedAddressIndex] = useState<number>(0);

  // The original NewRx's MessageID, threaded into the renewal as relatesToMessageId + rxReferenceNumber.
  const [newRxMessageId, setNewRxMessageId] = useState("");
  // The renewal's writtenDate must differ from the NewRx's (CATS autotype).
  const [newRxWrittenDate, setNewRxWrittenDate] = useState("");

  const [renewalWrittenDate, setRenewalWrittenDate] = useState("");
  const [relatesToMessageId, setRelatesToMessageId] = useState("");
  const [approvalNote, setApprovalNote] = useState("Renewal approved");
  // Renewal grants a fresh authorization independent of the NewRx (which is depleted at 0 refills).
  const [renewalRefills, setRenewalRefills] = useState("5");
  const [renewalDateError, setRenewalDateError] = useState("");

  const [newRxApproval, setNewRxApproval] = useState<IReviewedPrescription | null>(null);
  const [newRxOrder, setNewRxOrder] = useState<any>(null);
  const [renewalApproval, setRenewalApproval] = useState<IReviewedPrescription | null>(null);
  const [renewalOrder, setRenewalOrder] = useState<any>(null);
  const [renewalOrderSkipped, setRenewalOrderSkipped] = useState(false);
  const [renewalFollowUp, setRenewalFollowUp] = useState<IRoutedFollowUp | null>(null);

  const stepRefs = useRef<Array<HTMLDivElement | null>>([]);
  const stepRef = (step: number) => (el: HTMLDivElement | null) => {
    stepRefs.current[step] = el;
  };

  // Separate S3 hook instances so the NewRx and renewal uploads don't overwrite each other's response.
  const {
    isUploading,
    uploadResponse,
    error: s3Error,
    uploadToS3,
    retrieveFromS3,
  } = useS3Operations();
  const {
    isUploading: isUploadingRenewal,
    error: renewalS3Error,
    uploadToS3: uploadRenewalToS3,
  } = useS3Operations();

  // Separate ingestion hook instances so NewRx and renewal responses both persist.
  const {
    isIngesting: isIngestingNewRx,
    upmsResponse: newRxUpmsResponse,
    error: newRxError,
    ingestNewRx,
  } = useUPMSIngestion();
  const {
    isIngesting: isIngestingRenewal,
    upmsResponse: renewalUpmsResponse,
    error: renewalError,
    ingestRenewalRx,
  } = useUPMSIngestion();

  const {
    isEnrolling,
    enrollmentResponse,
    error: customerError,
    createCustomer,
    getCustomerAddresses,
    getAddressIdForZipCode,
  } = useCustomerCreation();

  const {
    options,
    handlePrimeChange,
    handleRxPassSubscriptionChange,
    handleRxPassForceActiveChange,
    handleHornbillSubscriptionChange,
    handleSetDefaultAddressChange,
    handleCommonAllergiesChange,
    handleCommonHealthConditionsChange,
    handlePregnantOrNursingChange,
    handleGenderChange,
  } = useCustomerOptions({ setDefaultAddress: true });

  const {
    patientDetails,
    prescriberDetails,
    medicationDetails,
    selectedPrescriber,
    selectedMedication,
    placeOfService,
    fieldErrors,
    setPatientField,
    updatePatientDetails,
    handlePrescriberChange,
    handleMedicationChange: handleMedicationChangeBase,
    handleMedicationFieldChange,
    handlePlaceOfServiceChange,
    updateFieldErrors,
  } = usePrescriptionForm("atorvastatin");

  // Seed the NewRx medication so it makes a valid renewal scenario:
  //  - 0 refills => depleted after one fill, so the renewal is genuinely needed (happy path).
  //  - writtenDate a few days in the PAST, so the renewal (dated today) can DIFFER from it for
  //    CATS autotype AND stay non-future (a future writtenDate is rejected downstream — the same
  //    "Written date cannot be in the future" rule NewRx/TransferRx enforce in ValidationUtils).
  // useCallback so it can be listed in the mount effect's dep array (satisfies react-hooks lint).
  // handleMedicationFieldChange is a stable useCallback([]) with a FUNCTIONAL setter, and daysAgo
  // only reads the imported getCurrentDateString — so there is no stale-closure capture of state.
  const applyRenewalSeed = useCallback(() => {
    handleMedicationFieldChange("numberOfRefills", "0");
    handleMedicationFieldChange("writtenDate", daysAgo(3));
  }, [handleMedicationFieldChange]);

  // Selecting a medication replaces medicationDetails wholesale (from the catalog), which would
  // wipe the seed above — so re-apply it after every medication change, not just on mount.
  const handleMedicationChange = (medicationKey: string) => {
    handleMedicationChangeBase(medicationKey);
    applyRenewalSeed();
  };

  useEffect(() => {
    applyRenewalSeed();
  }, [applyRenewalSeed]);

  useEffect(() => {
    const timer = setTimeout(() => {
      stepRefs.current[activeStep]?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
    return () => clearTimeout(timer);
  }, [activeStep]);

  const handleCreateCustomer = async () => {
    try {
      let residentialAddressId: string | undefined;
      if (
        selectedAddressIndex >= 0 &&
        selectedAddressIndex < PREDEFINED_ADDRESSES.length
      ) {
        const selectedAddress = PREDEFINED_ADDRESSES[selectedAddressIndex];
        const addressId = await getAddressIdForZipCode(selectedAddress.zipCode);
        residentialAddressId = addressId || undefined;
      }

      const response = await createCustomer({
        emailPrefix: emailPrefix.trim() || undefined,
        phoneNumber: phoneNumber.trim() || undefined,
        residentialAddressId,
        options,
      });

      setIsUpdatingFields(true);

      const personId = response.profile?.[0]?.personId;
      if (personId && response.profile && response.profile[0]) {
        const addresses = await getCustomerAddresses(personId);
        const profile = response.profile[0];
        const demographicProfile = profile.demographicProfile;

        if (demographicProfile) {
          const mailingAddress = addresses[0]?.mailingAddress || "";
          const parsedAddress = parseMailingAddress(mailingAddress);

          const genderCode =
            demographicProfile.gender === "Male"
              ? "M"
              : demographicProfile.gender === "Female"
              ? "F"
              : demographicProfile.gender || "M";

          updatePatientDetails({
            firstName: demographicProfile.legalFirstName || "",
            lastName: demographicProfile.legalLastName || "",
            dateOfBirth: demographicProfile.dateOfBirth || "",
            gender: genderCode,
            phoneNumber: profile.phoneNumber || response.phoneNumber || "",
            addressLine1: parsedAddress.addressLine1,
            addressLine2: parsedAddress.addressLine2,
            city: parsedAddress.city,
            state: parsedAddress.state,
            zipCode: parsedAddress.zipCode,
          });
        }
      }
      setIsUpdatingFields(false);
    } catch (err) {
      setIsUpdatingFields(false);
    }
  };

  const handleUploadToS3 = async () => {
    const patientValidation = ValidationUtils.validatePatientDetails(patientDetails);
    const medicationValidation = ValidationUtils.validateMedicationDetails({
      daysSupply: medicationDetails.daysSupply,
      ndc: medicationDetails.ndc,
      sigText: medicationDetails.sigText,
      quantity: medicationDetails.quantity,
      writtenDate: medicationDetails.writtenDate,
    });

    const combinedFieldErrors = {
      ...patientValidation.fieldErrors,
      ...medicationValidation.fieldErrors,
    };
    updateFieldErrors(combinedFieldErrors);

    if (!patientValidation.isValid || !medicationValidation.isValid) {
      return;
    }

    try {
      const prescriptionData = buildNewRxPayload({
        patientDetails,
        prescriberDetails,
        medicationDetails,
        placeOfService,
      });
      // Capture the NewRx MessageID + written date to seed the renewal.
      setNewRxMessageId(prescriptionData.Header.MessageID);
      setRelatesToMessageId(prescriptionData.Header.MessageID);
      setNewRxWrittenDate(medicationDetails.writtenDate);

      const response = await uploadToS3(prescriptionData);
      setS3FileName(response.s3Key || response.fileName || "uploaded_file.json");
    } catch (err) {
      // handled by hook
    }
  };

  const handleIngestNewRx = async () => {
    try {
      let dataToIngest = await retrieveFromS3(s3FileName);
      await ingestNewRx(dataToIngest, s3FileName);
      // Default the renewal written date to TODAY: it differs from the back-dated NewRx (autotype
      // requirement), is logically after it, and is not in the future.
      setRenewalWrittenDate(getCurrentDateString());
    } catch (err) {
      // handled by hook
    }
  };

  const handleUploadAndIngestRenewal = async () => {
    // Renewal written date is required (empty would build writtenDate:"" and bypass the checks below).
    if (!renewalWrittenDate) {
      setRenewalDateError("Renewal written date is required.");
      return;
    }
    // Validation: renewal written date must differ from the NewRx written date, or CATS won't autotype.
    if (newRxWrittenDate && renewalWrittenDate === newRxWrittenDate) {
      setRenewalDateError(
        "Renewal written date must differ from the original NewRx written date, or CATS will not autotype."
      );
      return;
    }
    // A future written date is rejected downstream (same rule NewRx/TransferRx enforce).
    if (renewalWrittenDate > getCurrentDateString()) {
      setRenewalDateError("Renewal written date cannot be in the future.");
      return;
    }
    if (!relatesToMessageId) {
      setRenewalDateError("Relates-to Message ID is required (the original NewRx MessageID).");
      return;
    }
    // Refills must be a whole number in [0, 98] — we send enteredRefills+1 (PPCore decrement offset),
    // and NCPDP NumberOfRefills is a 2-digit field, so the sent value must stay <= 99.
    // Guard the blank/whitespace case explicitly: Number("") is 0 and would silently pass [0,98].
    const refillsRaw = renewalRefills.trim();
    const enteredRefills = Number(refillsRaw);
    if (refillsRaw === "" || !Number.isInteger(enteredRefills) || enteredRefills < 0 || enteredRefills > 98) {
      setRenewalDateError("Number of refills must be a whole number between 0 and 98.");
      return;
    }
    setRenewalDateError("");

    try {
      const rxReferenceNumber = `RX${relatesToMessageId}`;
      // PPCore decrements an RxRenewalResponse's NumberOfRefills by 1 (eScribe authorizes N
      // *additional* refills; PillPack counts fills-after-the-first). To match the NewRx UX
      // where "N refills" => N+1 fills remaining, send N+1 so the post-decrement value is N.
      const sentRefills = String(enteredRefills + 1);
      // The S3 file and the ingest envelope must share MessageID/SentTime (UPMS re-reads the file
      // via sourceFileUrl and correlates it to the envelope header).
      const messageId = generateMessageId();
      const sentTime = convertToPST(new Date());
      const renewalMedication = { ...medicationDetails, numberOfRefills: sentRefills };
      const builderParams = {
        patientDetails,
        prescriberDetails,
        // Renewal grants its own refills, independent of the depleted (0-refill) NewRx.
        medicationDetails: renewalMedication,
        placeOfService,
        relatesToMessageId,
        rxReferenceNumber,
        writtenDate: renewalWrittenDate,
        approvalNote,
        messageId,
        sentTime,
      };
      // The camelCase envelope is the UPMS ingest API body; the PascalCase eScribe file is what
      // gets uploaded to S3 and re-read by UPMS (SAME shape as the NewRx S3 file, per the CCS SOP).
      const renewalData = buildRenewalRxPayload(builderParams);
      const renewalS3File = buildRenewalRxFileForS3(builderParams);

      // Upload the PascalCase eScribe file to S3 (like NewRx), then ingest the envelope pointing at it.
      // Uses the renewal-scoped S3 instance so step 1's NewRx uploadResponse is not overwritten.
      const uploadResp = await uploadRenewalToS3(renewalS3File);
      const fileName = uploadResp.s3Key || uploadResp.fileName || "renewal_file.json";

      await ingestRenewalRx(renewalData, fileName);
    } catch (err) {
      // handled by hook
    }
  };

  const handleReset = () => {
    window.location.reload();
  };

  /** Returns today minus N days as yyyy-MM-dd (used to back-date the NewRx into the past). */
  const daysAgo = (n: number): string => {
    const base = new Date(`${getCurrentDateString()}T00:00:00Z`);
    base.setUTCDate(base.getUTCDate() - n);
    return base.toISOString().split("T")[0];
  };

  // Read-only summary of the created customer (incl. login email/password), shown before the
  // renewal step. patientDetails is auto-filled from the customer at creation, so it is the source
  // of truth for name/dob/gender/phone/address.
  const renderCustomerInfo = () => {
    const profile = enrollmentResponse?.profile?.[0];
    if (!profile) {
      return null;
    }
    const fullName = `${patientDetails.firstName || ""} ${patientDetails.lastName || ""}`.trim();
    const address = [
      patientDetails.addressLine1,
      patientDetails.addressLine2,
      [patientDetails.city, patientDetails.state, patientDetails.zipCode].filter(Boolean).join(", "),
    ]
      .filter(Boolean)
      .join(", ");
    const rows: Array<[string, string]> = [
      ["Name", fullName || "—"],
      ["Email", enrollmentResponse?.email || "—"],
      ["Password", enrollmentResponse?.password || "—"],
      ["Date of Birth", patientDetails.dateOfBirth || "—"],
      ["Gender", patientDetails.gender || "—"],
      ["Phone", patientDetails.phoneNumber || profile.phoneNumber || "—"],
      ["Address", address || "—"],
      ["PersonId", profile.personId || "—"],
      ["ProfileId", profile.profileId || "—"],
    ];
    return (
      <Grid item xs={12}>
        <Paper style={{ padding: 24, backgroundColor: "#f5f5f5" }}>
          <Typography variant="h6" gutterBottom>
            Customer
          </Typography>
          <Grid container spacing={1}>
            {rows.map(([label, value]) => (
              <Grid item xs={12} sm={6} key={label}>
                <Typography variant="caption" color="textSecondary" display="block">
                  {label}
                </Typography>
                <Typography variant="body2" style={{ fontWeight: 500, wordBreak: "break-all" }}>
                  {value}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Grid>
    );
  };

  const renderStep1 = () => {
    const hasCustomer = !!enrollmentResponse;
    const hasS3Upload = !!uploadResponse && !!newRxMessageId;

    return (
      <div ref={stepRef(STEP.UPLOAD_NEWRX)}>
        <Grid container spacing={3}>
          {!hasCustomer ? (
            <>
              <Grid item xs={12}>
                <Paper style={{ padding: 24, backgroundColor: "#e3f2fd" }}>
                  <Typography variant="h6" gutterBottom>
                    Step 1a: Create Pharmacy Customer
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Auto-generate a new pharmacy customer. A NewRx is created first, then renewed.
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Email Prefix (Optional)"
                      variant="outlined"
                      value={emailPrefix}
                      onChange={(e) => setEmailPrefix(e.target.value)}
                      placeholder="e.g., john.doe"
                      helperText="Leave empty to auto-generate"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Phone Number (Optional)"
                      variant="outlined"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="e.g., 1234567890"
                      helperText="Leave empty to auto-generate"
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel>Select Address</InputLabel>
                  <Select
                    value={selectedAddressIndex}
                    onChange={(e) =>
                      setSelectedAddressIndex(e.target.value as number)
                    }
                    label="Select Address"
                  >
                    {PREDEFINED_ADDRESSES.map((address, index) => (
                      <MenuItem key={index} value={index}>
                        {address.addressLine1}
                        {address.addressLine2 && `, ${address.addressLine2}`}
                        {`, ${address.city}, ${address.stateName}, ${address.zipCode}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Customer Options
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={options.prime}
                          onChange={(e) => handlePrimeChange(e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Prime Member"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={options.rxPassSubscription}
                          onChange={(e) =>
                            handleRxPassSubscriptionChange(e.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label="RxPass Subscription (requires Prime)"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={options.rxPassForceActive}
                          onChange={(e) =>
                            handleRxPassForceActiveChange(e.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label="RxPass Force Active (requires Prime + RxPass)"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={options.hornbillSubscription}
                          onChange={(e) =>
                            handleHornbillSubscriptionChange(e.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label="Hornbill Subscription"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={options.setDefaultAddress}
                          onChange={(e) =>
                            handleSetDefaultAddressChange(e.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label="Set Default Address"
                    />
                  </Grid>
                </Grid>
              </Grid>

              <HealthProfileSection
                gender={options.gender}
                commonAllergies={options.commonAllergies}
                commonHealthConditions={options.commonHealthConditions}
                pregnantOrNursing={options.pregnantOrNursing}
                onGenderChange={handleGenderChange}
                onCommonAllergiesChange={handleCommonAllergiesChange}
                onCommonHealthConditionsChange={handleCommonHealthConditionsChange}
                onPregnantOrNursingChange={handlePregnantOrNursingChange}
              />

              {customerError && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    <strong>Customer Creation Error:</strong> {customerError}
                  </Alert>
                </Grid>
              )}

              <Grid item xs={12}>
                <StyledActionButton
                  label="Create Customer"
                  loadingLabel="Creating Customer..."
                  onClick={handleCreateCustomer}
                  loading={isEnrolling}
                  disabled={!!enrollmentResponse}
                  color={COLORS.orange}
                  startIcon={<PersonAddIcon />}
                />
              </Grid>
            </>
          ) : !hasS3Upload ? (
            <>
              <Grid item xs={12}>
                <Alert severity="success">
                  Customer created! PersonId:{" "}
                  {enrollmentResponse?.profile?.[0]?.personId}
                </Alert>
              </Grid>

              <Grid item xs={12}>
                <Paper style={{ padding: 24, backgroundColor: "#fff3e0" }}>
                  <Typography variant="h6" gutterBottom>
                    Step 1b: Configure & Upload NewRx
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Patient details are auto-filled. Choose prescriber, medication, and pharmacy.
                  </Typography>
                </Paper>
              </Grid>

              {isUpdatingFields && (
                <Grid item xs={12}>
                  <Alert severity="info">
                    ⏳ Updating patient details fields with customer information...
                  </Alert>
                </Grid>
              )}

              <PatientDetailsForm
                patientDetails={patientDetails}
                onFieldChange={setPatientField}
                fieldErrors={fieldErrors}
              />
              <PlaceOfServiceSelector
                placeOfService={placeOfService}
                onPlaceOfServiceChange={handlePlaceOfServiceChange}
              />
              <PrescriberSelector
                selectedPrescriber={selectedPrescriber}
                prescriberDetails={prescriberDetails}
                onPrescriberChange={handlePrescriberChange}
              />
              <MedicationSelector
                selectedMedication={selectedMedication}
                medicationDetails={medicationDetails}
                onMedicationChange={handleMedicationChange}
                onMedicationFieldChange={handleMedicationFieldChange}
                fieldErrors={fieldErrors}
                editableRefills
              />
              <PharmacyDetails />

              {s3Error && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    <strong>S3 Upload Error:</strong> {s3Error}
                  </Alert>
                </Grid>
              )}

              <Grid item xs={12}>
                <StyledActionButton
                  label="Upload NewRx to S3"
                  loadingLabel="Uploading to S3..."
                  onClick={handleUploadToS3}
                  loading={isUploading}
                  disabled={!!uploadResponse}
                  color={COLORS.orange}
                  startIcon={<CloudUploadIcon />}
                />
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12}>
                <Alert severity="success">
                  NewRx uploaded to S3! File: {s3FileName} (MessageID: {newRxMessageId})
                </Alert>
              </Grid>
              <DataAccordion title="Customer Creation Response" data={enrollmentResponse} defaultExpanded />
              <DataAccordion title="S3 Upload Response" data={uploadResponse} defaultExpanded />
              <Grid item xs={12}>
                <StyledActionButton
                  label="Continue to NewRx Ingestion"
                  onClick={() => setActiveStep(STEP.INGEST_NEWRX)}
                  color={COLORS.orange}
                  startIcon={<ArrowForwardIcon />}
                />
              </Grid>
            </>
          )}
        </Grid>
        {(isEnrolling || isUploading) && <LoadingSpinner />}
      </div>
    );
  };

  const renderStep2 = () => {
    const hasNewRxResponse = !!newRxUpmsResponse;
    return (
      <div ref={stepRef(STEP.INGEST_NEWRX)}>
        <Grid container spacing={3}>
          {!hasNewRxResponse ? (
            <>
              <Grid item xs={12}>
                <Paper style={{ padding: 24, backgroundColor: "#e8f5e9" }}>
                  <Typography variant="h6" gutterBottom>
                    Ingest NewRx to UPMS
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Retrieve the NewRx from S3 and ingest it. Its MessageID seeds the renewal.
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2">
                  <strong>S3 File:</strong> {s3FileName}
                </Typography>
              </Grid>

              {newRxError && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    <strong>UPMS Ingestion Error:</strong> {newRxError}
                  </Alert>
                </Grid>
              )}

              <Grid item xs={12}>
                <div style={{ display: "flex", gap: 16 }}>
                  <StyledActionButton
                    label="Ingest NewRx to UPMS"
                    loadingLabel="Ingesting to UPMS..."
                    onClick={handleIngestNewRx}
                    loading={isIngestingNewRx}
                    disabled={!!newRxUpmsResponse}
                    color={COLORS.orange}
                    startIcon={<SendIcon />}
                  />
                  <StyledActionButton
                    label="Back"
                    onClick={() => setActiveStep(STEP.UPLOAD_NEWRX)}
                    disabled={isIngestingNewRx || !!newRxUpmsResponse}
                    color={COLORS.orange}
                    startIcon={<ArrowBackIcon />}
                  />
                </div>
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12}>
                <Alert severity="success">NewRx successfully ingested to UPMS!</Alert>
              </Grid>
              <DataAccordion title="NewRx UPMS Ingestion Response" data={newRxUpmsResponse} />

              <Grid item xs={12}>
                <StyledActionButton
                  label="Continue to Approve NewRx"
                  onClick={() => setActiveStep(STEP.APPROVE_NEWRX)}
                  color={COLORS.orange}
                  startIcon={<ArrowForwardIcon />}
                />
              </Grid>
            </>
          )}
        </Grid>
        {isIngestingNewRx && <LoadingSpinner />}
      </div>
    );
  };

  const personId = enrollmentResponse?.profile?.[0]?.personId;
  const drugDescription = medicationDetails.drugDescription;

  const renderContinue = (label: string, nextStep: number, enabled: boolean) => (
    <Grid item xs={12}>
      <StyledActionButton
        label={label}
        onClick={() => setActiveStep(nextStep)}
        disabled={!enabled}
        color={COLORS.orange}
        startIcon={<ArrowForwardIcon />}
      />
    </Grid>
  );

  const renderApproveNewRx = () => (
    <div ref={stepRef(STEP.APPROVE_NEWRX)}>
      <Grid container spacing={3}>
        <CcsReviewPanel
          mode="approve"
          title="Approve NewRx"
          approvedTitle="NewRx Approved"
          readyHint={RPH_CHECK_HINT}
          handoff={{ personId, drugDescription }}
          result={newRxApproval}
          onApproved={(response, prescriptionId) => setNewRxApproval({ response, prescriptionId })}
        />
        {renderContinue("Continue to Create Order", STEP.ORDER_NEWRX, !!newRxApproval)}
      </Grid>
    </div>
  );

  const renderOrderNewRx = () => (
    <div ref={stepRef(STEP.ORDER_NEWRX)}>
      <Grid container spacing={3}>
        <CreateOrderPanel
          title="Create Order for NewRx"
          readyHint="Allow ~1 min after approval. Ordering uses up the NewRx's fill so the renewal is needed."
          personId={personId}
          prescriptionId={newRxApproval?.prescriptionId}
          response={newRxOrder}
          onOrdered={setNewRxOrder}
        />
        {renderContinue("Continue to Renewal", STEP.INGEST_RENEWAL, !!newRxOrder)}
      </Grid>
    </div>
  );

  const skipRenewalOrder = () => {
    setRenewalOrderSkipped(true);
    setActiveStep(STEP.COMPLETE);
  };

  const renderRenewalReviewActions = () => {
    if (renewalFollowUp) {
      return renderContinue("Complete Workflow", STEP.COMPLETE, true);
    }
    return (
      <Grid item xs={12}>
        <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
          <StyledActionButton
            label="Continue to Create Order"
            onClick={() => setActiveStep(STEP.ORDER_RENEWAL)}
            disabled={!renewalApproval}
            color={COLORS.orange}
            startIcon={<ArrowForwardIcon />}
          />
          {renewalApproval && (
            <StyledActionButton
              label="Skip order, finish workflow"
              onClick={skipRenewalOrder}
              variant="outlined"
              color={COLORS.orange}
            />
          )}
        </div>
      </Grid>
    );
  };

  const renderReviewRenewal = () => (
    <div ref={stepRef(STEP.REVIEW_RENEWAL)}>
      <Grid container spacing={3}>
        <CcsReviewPanel
          mode="either"
          title="Approve or Route Renewal to Follow-Up"
          approvedTitle="Renewal Approved"
          routedTitle="Renewal Routed to Follow-Up"
          readyHint={RPH_CHECK_HINT}
          handoff={{ personId, drugDescription, excludePrescriptionId: newRxApproval?.prescriptionId }}
          result={renewalFollowUp ?? renewalApproval}
          onApproved={(response, prescriptionId) => setRenewalApproval({ response, prescriptionId })}
          onRouted={(prescriptionId, issues) => setRenewalFollowUp({ prescriptionId, issues })}
        />
        {renderRenewalReviewActions()}
      </Grid>
    </div>
  );

  const renderOrderRenewal = () => (
    <div ref={stepRef(STEP.ORDER_RENEWAL)}>
      <Grid container spacing={3}>
        <CreateOrderPanel
          title="Create Order for Renewal"
          readyHint="Allow ~1 min after approval."
          personId={personId}
          prescriptionId={renewalApproval?.prescriptionId}
          response={renewalOrder}
          onOrdered={setRenewalOrder}
        />
        {renderContinue("Complete Workflow", STEP.COMPLETE, !!renewalOrder)}
      </Grid>
    </div>
  );

  const renderStep3 = () => {
    const hasRenewalResponse = !!renewalUpmsResponse;
    return (
      <div ref={stepRef(STEP.INGEST_RENEWAL)}>
        <Grid container spacing={3}>
          {!hasRenewalResponse ? (
            <>
              <Grid item xs={12}>
                <Paper style={{ padding: 24, backgroundColor: "#fff3e0" }}>
                  <Typography variant="h6" gutterBottom>
                    Ingest RxRenewalResponse
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Prefilled from the NewRx. Relates-to Message ID links to the original NewRx; the
                    written date must differ from it so CATS can autotype.
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Relates-to Message ID (original NewRx)"
                  variant="outlined"
                  value={relatesToMessageId}
                  onChange={(e) => setRelatesToMessageId(e.target.value)}
                  helperText="The NewRx MessageID this renewal responds to"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Renewal Written Date"
                  type="date"
                  variant="outlined"
                  value={renewalWrittenDate}
                  onChange={(e) => setRenewalWrittenDate(e.target.value)}
                  error={!!renewalDateError}
                  helperText={
                    renewalDateError ||
                    `Must differ from NewRx written date (${newRxWrittenDate || "n/a"})`
                  }
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Number of Refills"
                  type="number"
                  variant="outlined"
                  value={renewalRefills}
                  onChange={(e) => setRenewalRefills(e.target.value)}
                  inputProps={{ min: 0, max: 99 }}
                  helperText="Refills the renewal grants (independent of the depleted NewRx)"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Approval Note"
                  variant="outlined"
                  value={approvalNote}
                  onChange={(e) => setApprovalNote(e.target.value)}
                  fullWidth
                />
              </Grid>

              {renewalError && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    <strong>Renewal Ingestion Error:</strong> {renewalError}
                  </Alert>
                </Grid>
              )}
              {renewalS3Error && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    <strong>S3 Error:</strong> {renewalS3Error}
                  </Alert>
                </Grid>
              )}

              <Grid item xs={12}>
                <div style={{ display: "flex", gap: 16 }}>
                  <StyledActionButton
                    label="Upload & Ingest Renewal"
                    loadingLabel={isUploadingRenewal ? "Uploading to S3..." : "Ingesting Renewal..."}
                    onClick={handleUploadAndIngestRenewal}
                    loading={isUploadingRenewal || isIngestingRenewal}
                    disabled={!!renewalUpmsResponse}
                    color={COLORS.orange}
                    startIcon={<SendIcon />}
                  />
                  <StyledActionButton
                    label="Back"
                    onClick={() => setActiveStep(STEP.ORDER_NEWRX)}
                    disabled={isUploadingRenewal || isIngestingRenewal || !!renewalUpmsResponse}
                    color={COLORS.orange}
                    startIcon={<ArrowBackIcon />}
                  />
                </div>
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12}>
                <Alert severity="success">Renewal successfully ingested to UPMS!</Alert>
              </Grid>
              <DataAccordion title="Renewal UPMS Ingestion Response" data={renewalUpmsResponse} />

              <Grid item xs={12}>
                <StyledActionButton
                  label="Continue to Approve or Follow-Up"
                  onClick={() => setActiveStep(STEP.REVIEW_RENEWAL)}
                  color={COLORS.orange}
                  startIcon={<ArrowForwardIcon />}
                />
              </Grid>
            </>
          )}
        </Grid>
        {(isUploadingRenewal || isIngestingRenewal) && <LoadingSpinner />}
      </div>
    );
  };

  const renderComplete = () => {
    const renewalOutcome = renewalFollowUp
      ? "renewal routed to follow-up"
      : renewalOrderSkipped
      ? "renewal approved (not ordered)"
      : "renewal approved and ordered";
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper style={{ padding: 32, backgroundColor: "#e8f5e9", textAlign: "center" }}>
            <Typography variant="h5" gutterBottom style={{ color: "#2e7d32" }}>
              ✓ Renewal Workflow Complete!
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Customer created, NewRx approved and ordered, {renewalOutcome}.
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Summary
          </Typography>
        </Grid>

        <DataAccordion title="Customer Creation" data={enrollmentResponse} />
        <DataAccordion title="Ingest NewRx" data={newRxUpmsResponse} />
        <DataAccordion title="Approve NewRx" data={newRxApproval} />
        <DataAccordion title="Create Order for NewRx" data={newRxOrder} />
        <DataAccordion title="Ingest Renewal" data={renewalUpmsResponse} />
        {renewalApproval && <DataAccordion title="Approve Renewal" data={renewalApproval} />}
        {renewalFollowUp && <DataAccordion title="Renewal Routed to Follow-Up" data={renewalFollowUp} />}
        {renewalOrder && <DataAccordion title="Create Order for Renewal" data={renewalOrder} />}

        <Grid item xs={12}>
          <StyledActionButton
            label="Start New Workflow"
            onClick={handleReset}
            color={COLORS.orange}
            startIcon={<RefreshIcon />}
          />
        </Grid>
      </Grid>
    );
  };

  const doneIcon = (done: boolean) => (done ? <CheckCircleIcon style={{ color: "#4caf50" }} /> : undefined);

  const steps: Array<{ id: number; label: string; done: boolean; render: () => React.ReactNode }> = [
    {
      id: STEP.UPLOAD_NEWRX,
      label: "Create Customer & Upload NewRx to S3",
      done: !!enrollmentResponse && !!uploadResponse && !!newRxMessageId,
      render: renderStep1,
    },
    { id: STEP.INGEST_NEWRX, label: "Ingest NewRx to UPMS", done: !!newRxUpmsResponse, render: renderStep2 },
    { id: STEP.APPROVE_NEWRX, label: "Approve NewRx", done: !!newRxApproval, render: renderApproveNewRx },
    { id: STEP.ORDER_NEWRX, label: "Create Order for NewRx", done: !!newRxOrder, render: renderOrderNewRx },
    { id: STEP.INGEST_RENEWAL, label: "Ingest Renewal to UPMS", done: !!renewalUpmsResponse, render: renderStep3 },
    {
      id: STEP.REVIEW_RENEWAL,
      label: "Approve or Route Renewal to Follow-Up",
      done: !!renewalApproval || !!renewalFollowUp,
      render: renderReviewRenewal,
    },
    {
      id: STEP.ORDER_RENEWAL,
      label: renewalFollowUp
        ? "Create Order for Renewal (skipped, routed to follow-up)"
        : renewalOrderSkipped
        ? "Create Order for Renewal (skipped)"
        : "Create Order for Renewal",
      done: !!renewalOrder,
      render: renderOrderRenewal,
    },
    { id: STEP.COMPLETE, label: "Workflow Complete", done: false, render: renderComplete },
  ];

  return (
    <Grid container direction="column" justifyContent="center" spacing={3}>
      {activeStep > STEP.UPLOAD_NEWRX && renderCustomerInfo()}
      <Grid item xs={12} style={{ width: "100%" }}>
        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((step) => (
            <Step key={step.id}>
              <StepLabel icon={doneIcon(step.done)}>{step.label}</StepLabel>
              <StepContent>{step.render()}</StepContent>
            </Step>
          ))}
        </Stepper>
      </Grid>
    </Grid>
  );
};

export default UnifiedRenewalWorkflowSection;
